import { FISCAL_STATUSES } from '@wodi/domain';
import { z } from 'zod';

import { pageSchema } from './pagination.ts';

// eTIMS signing modes (docs/plans/phase3-etims-vscu.md §3; ADR 0041). Document numbers,
// states and KRA's fields only: nothing here names or identifies a patient.

/** How a facility signs: not at all, through KRA online, or through the VSCU on site. */
export const ETIMS_FACILITY_MODES = ['off', 'oscu', 'vscu'] as const;
export type EtimsFacilityMode = (typeof ETIMS_FACILITY_MODES)[number];

export const ETIMS_DOCUMENT_TYPES = ['invoice', 'credit_note'] as const;

export const fiscalStatusSchema = z.enum(FISCAL_STATUSES);

/** Why a document isn't signed (or sent), and what to do: shown as written. */
export const etimsProblemSchema = z.object({
  code: z.string(),
  message: z.string(),
  nextStep: z.string(),
});

export const etimsOfflineAllowanceSchema = z.discriminatedUnion('allowed', [
  z.object({
    allowed: z.literal(true),
    /** Null while nothing is waiting to reach KRA. */
    signingStopsAt: z.iso.datetime().nullable(),
    documentsLeft: z.number().int().min(0).nullable(),
  }),
  z.object({
    allowed: z.literal(false),
    limit: z.enum(['hours', 'documents']),
    message: z.string(),
    nextStep: z.string(),
  }),
]);

export const etimsSettingsSchema = z.object({
  mode: z.enum(ETIMS_FACILITY_MODES),
  /** 0 before the first change. */
  version: z.number().int().min(0),
  /** The modes this server has an adapter for; the others can't be chosen. */
  availableModes: z.array(z.enum(['oscu', 'vscu'])),
  /** VSCU's documents signed offline and not yet sent to KRA, and what may still be signed. */
  offline: z.object({
    count: z.number().int().min(0),
    oldestSignedAt: z.iso.datetime().nullable(),
    limits: z.object({
      maxHours: z.number().positive(),
      maxDocuments: z.number().int().min(1).nullable(),
    }),
    allowance: etimsOfflineAllowanceSchema,
  }),
  /** Documents needing attention, by shown state. */
  counts: z.object({
    queued: z.number().int().min(0),
    signed_offline: z.number().int().min(0),
    failed: z.number().int().min(0),
    transmission_failed: z.number().int().min(0),
  }),
});
export type EtimsSettingsView = z.infer<typeof etimsSettingsSchema>;

export const updateEtimsSettingsBodySchema = z.object({
  mode: z.enum(ETIMS_FACILITY_MODES),
  reason: z.string().trim().min(3, 'Say why the mode is changing').max(500),
  expectedVersion: z.number().int().min(0),
});

export const etimsDocumentSchema = z.object({
  signingId: z.uuid(),
  documentType: z.enum(ETIMS_DOCUMENT_TYPES),
  number: z.string(),
  issuedAt: z.iso.datetime(),
  /** As shown: a waiting document whose event ran out of retries is failed. */
  state: fiscalStatusSchema,
  /** Null until it was first sent. */
  mode: z.enum(['oscu', 'vscu']).nullable(),
  attempt: z.number().int().min(1),
  signedAt: z.iso.datetime().nullable(),
  transmittedAt: z.iso.datetime().nullable(),
  problem: etimsProblemSchema.nullable(),
  canSignAgain: z.boolean(),
});
export type EtimsDocumentView = z.infer<typeof etimsDocumentSchema>;

export const etimsDocumentPageSchema = pageSchema(etimsDocumentSchema);

/** What a receipt prints for one invoice the payment paid (plan §5). */
export const receiptFiscalSchema = z.object({
  invoiceNumber: z.string(),
  state: fiscalStatusSchema,
  mode: z.enum(['oscu', 'vscu']).nullable(),
  signedAt: z.iso.datetime().nullable(),
  /** KRA's (or the VSCU's) fields exactly as returned; empty until signed. */
  fields: z.array(z.object({ name: z.string(), value: z.string() })),
});
export type ReceiptFiscal = z.infer<typeof receiptFiscalSchema>;
